// A counterparty's files on its profile (TX-US-0033, TX-US-0034): a trade licence, a KYC paper,
// audited accounts, in one list whatever the counterparty's status, with the selected one shown
// beside it. Everyone who reads the counterparty previews and downloads them; Operations,
// Compliance and Risk drop new ones on the zone and remove any of them. A dropped file uploads
// at once - the counterparty it belongs to already exists.
//
// A delete takes the document off the list and nothing else: the API keeps the record and the
// file. The API is the authority on who may write - `canEdit` only decides what is offered.

import { useCallback, useEffect, useState } from 'react';

import {
  deleteCounterpartyDocument,
  getCounterpartyDocumentDownload,
  listCounterpartyDocuments,
} from '../../api.js';
import { formatBytes } from '../../deliveries/attachments/uploads.js';
import useAttachmentUploads from '../../deliveries/attachments/useAttachmentUploads.js';
import DropZone from '../../ui/DropZone.jsx';
import Icon from '../../ui/Icon.jsx';
import DocumentPreview from './DocumentPreview.jsx';
import { ACCEPT, FORMATS_HINT, documentMeta, selectedAmong } from './files.js';
import PendingFile from './PendingFile.jsx';
import { DOCUMENTS_API } from './uploadApi.js';

// A signed URL is cross-origin, so `download` on a link is ignored and the browser navigates;
// opening it in a new tab keeps the profile where it is.
function open(url) {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener';
  link.click();
}

export default function CounterpartyDocuments({ counterpartyId, canEdit }) {
  const [documents, setDocuments] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const { items, pending, draining, add, drop, drain } = useAttachmentUploads(DOCUMENTS_API);

  const load = useCallback(
    () => listCounterpartyDocuments(counterpartyId)
      .then(setDocuments)
      .catch((err) => setError(err.message)),
    [counterpartyId],
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (pending === 0 || draining) return;
    drain(counterpartyId).then(load);
  }, [counterpartyId, pending, draining, drain, load]);

  const download = (doc) => getCounterpartyDocumentDownload(counterpartyId, doc.document_id)
    .then((found) => open(found.download_url))
    .catch((err) => setError(err.message));

  // Two presses: the first arms the button, the second deletes.
  const remove = async (doc) => {
    if (confirming !== doc.document_id) {
      setConfirming(doc.document_id);
      return;
    }
    setConfirming(null);
    try {
      await deleteCounterpartyDocument(counterpartyId, doc.document_id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const listed = documents ?? [];
  const empty = documents && listed.length === 0 && items.length === 0;
  const selected = selectedAmong(listed, selectedId);

  return (
    <section className="attachments counterparty-documents"
             aria-labelledby="counterparty-documents-heading">
      <h3 id="counterparty-documents-heading">Files</h3>

      {error && <p className="error">{error}</p>}

      <div className={`documents-body${selected ? ' with-preview' : ''}`}>
        <div className="documents-list">
          {canEdit && (
            <DropZone accept={ACCEPT} onFiles={add} disabled={draining} hint={FORMATS_HINT}>
              Drop files here, or choose them
            </DropZone>
          )}

          {empty && !canEdit && <p className="muted">No files attached.</p>}

          {!empty && (
            <ul className="attachment-list" aria-label="Files">
              {listed.map((doc) => (
                <li key={doc.document_id}
                    className={selected?.document_id === doc.document_id ? 'selected' : ''}>
                  <button type="button" className="attachment-select"
                          onClick={() => setSelectedId(doc.document_id)}
                          aria-pressed={selected?.document_id === doc.document_id}
                          aria-label={`Preview ${doc.filename}`}>
                    <span className="attachment-name">
                      <strong>{doc.filename}</strong>
                      <small>{documentMeta(doc, formatBytes)}</small>
                    </span>
                  </button>
                  <span className="row-actions">
                    <button type="button" className="icon-button quiet"
                            onClick={() => download(doc)}
                            aria-label={`Download ${doc.filename}`} title="Download">
                      <Icon name="download" />
                    </button>
                    {canEdit && (
                      <button type="button"
                              className={`icon-button quiet${confirming === doc.document_id ? ' confirming' : ''}`}
                              onClick={() => remove(doc)}
                              onBlur={() => setConfirming(null)}
                              aria-label={confirming === doc.document_id
                                ? `Confirm delete ${doc.filename}`
                                : `Delete ${doc.filename}`}
                              title={confirming === doc.document_id ? 'Click again to delete' : 'Delete'}>
                        <Icon name="trash" />
                      </button>
                    )}
                  </span>
                </li>
              ))}
              {items.map((item) => <PendingFile key={item.key} item={item} onRemove={drop} />)}
            </ul>
          )}
        </div>

        {selected && (
          <DocumentPreview counterpartyId={counterpartyId} document={selected}
                           onDownload={download} />
        )}
      </div>
    </section>
  );
}
